<script setup lang="ts" generic="T extends string | number">
import { LockClosedIcon } from '@blpde/nx-icons';
import { computed, nextTick, ref, useId, type Component } from 'vue';
import { NxButtonSize } from './../Button';
import NxTooltip from './../Tooltip/Tooltip.vue';
import NxSaveStateBadge from '../Inputs/NxSaveStateBadge.vue';
import { useInputSaveStatus } from '../Inputs/composables/useInputSaveStatus';

export interface NxToggleGroupOption<V extends string | number> {
  value: V;
  label: string;
  icon?: Component;
  disabled?: boolean;
  /** Hover tooltip; useful when the group renders icon-only segments. */
  tooltip?: string;
  /**
   * Shown, reachable and clickable, but not choosable — a scope the user's role
   * does not grant. Rendered hatched with a lock; a click (or Space, or arrowing
   * onto it) keeps the model and emits `locked-click` so the caller can say which
   * permission is missing (put the short reason into `tooltip`). `disabled` stays
   * the silent variant: out of the tab order, click swallowed.
   */
  locked?: boolean;
}

export interface NxToggleGroupProps<V extends string | number> {
  options: NxToggleGroupOption<V>[];
  /** Optional group label, rendered as a non-interactive segment on the left. */
  label?: string;
  /** Height scale, matches NxButton. Defaults to `'s'`. */
  height?: NxButtonSize;
  /** Disables the whole group. */
  disabled?: boolean;
  /** Placement for option tooltips. Defaults to `'top'`. */
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Many options: the track wraps its segments onto more lines instead of
   * overflowing the row. Defaults to `false`.
   */
  wrap?: boolean;
  /**
   * Icon-only segments (a view switch like grid / list / columns): the label
   * stays the accessible name and becomes the tooltip when none is given.
   * Options without an icon still show their label. Defaults to `false`.
   */
  iconOnly?: boolean;
}

const props = withDefaults(defineProps<NxToggleGroupProps<T>>(), {
  height: 's',
  label: undefined,
  tooltipSide: 'top',
});

/** Single-select: holds the `value` of the active option. */
const model = defineModel<T>({ required: true });

const emit = defineEmits<{
  /** A locked option was clicked or chosen by keyboard; the model is unchanged. */
  lockedClick: [option: NxToggleGroupOption<T>];
}>();

const groupEl = ref<HTMLFieldSetElement | null>(null);

/**
 * A locked radio stays a live radio (focusable, announced as unavailable via
 * `aria-disabled`), so the click that would check it is cancelled instead: a
 * cancelled click fires no `change`, which is what `v-model` listens to. Label
 * clicks, Space and the arrow keys all arrive here as a click on the input.
 *
 * The DOM is then pulled back onto the model, as `NxRadioItem` does (I-1372):
 * which radio a cancelled click re-checks is the engine's legacy behaviour —
 * browsers restore the previous one, jsdom only unchecks the clicked one — and
 * Vue has nothing to patch, because the model never changed.
 */
function onInputClick(event: MouseEvent, opt: NxToggleGroupOption<T>) {
  if (!opt.locked) return;
  event.preventDefault();
  emit('lockedClick', opt);
  void nextTick(() => {
    const inputs = groupEl.value?.querySelectorAll<HTMLInputElement>('input[type="radio"]') ?? [];
    inputs.forEach((input, i) => {
      input.checked = props.options[i]?.value === model.value;
    });
  });
}

function hideLabel(opt: NxToggleGroupOption<T>): boolean {
  return Boolean(props.iconOnly && opt.icon);
}

function tooltipOf(opt: NxToggleGroupOption<T>): string | undefined {
  return opt.tooltip ?? (hideLabel(opt) ? opt.label : undefined);
}

// Save status (loading/success/error) from an ancestor NxLoadingWrapper. While
// it saves, the group locks like the checkbox, the switch and NxRadioGroup: the
// same native fieldset[disabled] its own `disabled` prop sets, so neither a
// click nor the keyboard (which arrives as a click on the radio) changes it.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
const isDisabled = computed(() => props.disabled || statusDisabled.value);

// Unique radio group name + label id per instance.
const name = useId();
const labelId = useId();
</script>

<template>
  <fieldset
    ref="groupEl"
    class="nx-toggle-group"
    :class="[`height-${props.height}`, { 'is-wrap': props.wrap }]"
    :disabled="isDisabled"
    role="radiogroup"
    v-bind="ariaBindings"
    :aria-labelledby="props.label ? labelId : undefined"
    :data-save-status="visualState"
  >
    <span v-if="props.label" :id="labelId" class="tg-label">{{ props.label }}</span>
    <label
      v-for="opt in props.options"
      :key="String(opt.value)"
      class="segment"
      :class="{
        selected: opt.value === model,
        disabled: opt.disabled,
        locked: opt.locked,
        'icon-only': hideLabel(opt),
      }"
    >
      <input
        v-model="model"
        type="radio"
        :name="name"
        :value="opt.value"
        :disabled="opt.disabled"
        :aria-disabled="opt.locked ? 'true' : undefined"
        class="sr-only"
        @click="onInputClick($event, opt)"
      />
      <NxTooltip v-if="tooltipOf(opt)" :text="tooltipOf(opt)!" :side="props.tooltipSide">
        <component :is="opt.icon" v-if="opt.icon" class="nxt-icon-md" />
        <span :class="{ 'sr-only': hideLabel(opt) }">{{ opt.label }}</span>
        <LockClosedIcon v-if="opt.locked" filled class="tg-lock nxt-icon-sm" aria-hidden="true" />
      </NxTooltip>
      <template v-else>
        <component :is="opt.icon" v-if="opt.icon" class="nxt-icon-md" />
        <span :class="{ 'sr-only': hideLabel(opt) }">{{ opt.label }}</span>
        <LockClosedIcon v-if="opt.locked" filled class="tg-lock nxt-icon-sm" aria-hidden="true" />
      </template>
    </label>
    <NxSaveStateBadge
      class="save-badge"
      :state="visualState"
      :error-message="errorMessage"
      :fade-out="fadeOut"
    />
    <span class="sr-only" aria-live="polite">{{ srText }}</span>
  </fieldset>
</template>

<style scoped>
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
/*
 * A segmented control: one track in the input fill (like the search field beside
 * it), the chosen segment lifted out of it as a pill — so a toolbar row
 * (search | view switch | sort) reads as one piece in light and dark.
 */
.nx-toggle-group {
  --tg-pad: calc(var(--nxt-size-unit) * 0.1875);
  --tg-seg-pad: var(--nxt-space-sm);

  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: var(--tg-pad);
  padding: var(--tg-pad);
  margin: 0;
  min-inline-size: 0;
  background-color: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--tg-radius);
}

/* Height scale — font and radius per step; the height itself follows the
   font like NxButton and the input fields (1lh + 2 × space-xs + border), so a
   group sits flush beside a search field or button of the same `height`. */
.nx-toggle-group.height-xs {
  --tg-font: var(--nxt-font-size-xs);
  --tg-radius: var(--nxt-radius-md);
}
.nx-toggle-group.height-s {
  --tg-font: var(--nxt-font-size-sm);
  --tg-radius: var(--nxt-radius-md);
}
.nx-toggle-group.height-m {
  --tg-font: var(--nxt-font-size-md);
  --tg-radius: var(--nxt-radius-lg);
}
.nx-toggle-group.height-l {
  --tg-font: var(--nxt-font-size-lg);
  --tg-radius: var(--nxt-radius-lg);
}
.nx-toggle-group.height-xl {
  --tg-font: var(--nxt-font-size-xl);
  --tg-radius: var(--nxt-radius-xl);
}

.tg-label,
.segment {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  /* A field's height minus the track's padding (the track's border stands in
     for the field's border). */
  height: calc(1lh + 2 * var(--nxt-space-xs) - 2 * var(--tg-pad));
  padding-inline: var(--tg-seg-pad);
  font-family: inherit;
  font-size: var(--tg-font);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-control);
  white-space: nowrap;
  border-radius: calc(var(--tg-radius) - var(--tg-pad));
}

/* Non-interactive label, inside the track on the left. */
.tg-label {
  justify-content: flex-start;
  padding-inline: var(--nxt-space-xs);
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-semibold);
  text-transform: uppercase;
}

/* Interactive segments — quiet in the track; the chosen one is a raised pill. */
.segment {
  position: relative;
  color: var(--nxt-fg-subtle);
  background-color: transparent;
  cursor: pointer;
  transition:
    color 0.15s,
    background-color 0.15s,
    box-shadow 0.15s;
}
.segment.icon-only {
  --tg-seg-pad: var(--nxt-space-xs);
  aspect-ratio: 1.25;
}
.segment:hover:not(.disabled):not(.selected):not(.locked) {
  color: var(--nxt-fg);
  background-color: var(--nxt-input-bg-hover);
}
.segment.selected {
  z-index: var(--nxt-z-raised);
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
  background-color: var(--nxt-surface-bg);
  box-shadow:
    var(--nxt-shadow-sm),
    0 0 0 var(--nxt-hairline) var(--nxt-border);
}
.segment.selected > :deep(svg),
.segment.selected :deep(.tooltip-wrapper > svg) {
  color: var(--nxt-brand-bg);
}
.segment.disabled {
  cursor: not-allowed;
  color: var(--nxt-disabled-fg);
}
/* Locked: hatched like the resource grid's unavailable days, readable text —
   the option is there, just not for this user. */
.segment.locked {
  cursor: not-allowed;
  color: var(--nxt-fg-subtle);
  background-image: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--nxt-border-strong) 70%, transparent) 0 var(--nxt-hairline),
    transparent var(--nxt-hairline) calc(var(--nxt-size-unit) * 0.375)
  );
}
.tg-lock {
  flex: 0 0 auto;
}

/*
 * The tooltip wrapper covers the whole segment (not just its icon+label), so
 * hovering anywhere on the segment shows the tooltip — while staying in normal
 * flow, so it still sizes the segment like the bare content would.
 *
 * `position: absolute; inset: 0` would take it out of flow: the segment's
 * shrink-to-fit width then collapses to its own padding (a „Voll“ segment
 * measured 26px instead of ~52px). Instead the wrapper gets a negative
 * margin-inline equal to its padding-inline (both --tg-seg-pad), so it adds
 * exactly its content's width to the segment yet still paints out to the
 * segment's edges. align-self: stretch does the same for the height.
 */
.segment :deep(.tooltip-wrapper) {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  gap: var(--nxt-space-xs);
  margin-inline: calc(var(--tg-seg-pad) * -1);
  padding-inline: var(--tg-seg-pad);
}

/* `wrap`: the track wraps onto as many lines as needed. */
.nx-toggle-group.is-wrap {
  flex-wrap: wrap;
}

/* Keyboard focus ring on the segment owning the focused radio. */
.segment:has(input:focus-visible) {
  z-index: var(--nxt-z-above);
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: 0;
}

/* Whole-group disabled (native fieldset[disabled] cascade). */
.nx-toggle-group:disabled {
  opacity: 0.5;
}
.nx-toggle-group:disabled .segment {
  cursor: not-allowed;
}

.sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(
    var(--nxt-size-unit) * -0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
